import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Box, Typography, Paper, Grid, TextField, Button, Autocomplete, 
    Alert, CircularProgress, Dialog, DialogTitle, 
    DialogContent, DialogActions, InputAdornment, IconButton,
    List, ListItem, ListItemText, Divider, ListItemSecondaryAction,
    Card, Snackbar, Tooltip, Chip
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
    MedicalServices as MedicalServicesIcon,
    Assignment as AssignmentIcon,
    PlaylistAdd as PlaylistAddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Add as AddIcon,
    History as HistoryIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

interface Patient { id: string; name: string; lastName: string; cedula?: string; idNumber?: string; }
interface Procedure { id: string; name: string; }
interface Template { id: string | null; title: string; content: string; category: string; }
interface ExecutionRecord { id: string; procedureName: string; date: string; doctorName: string; doctorId: string; description: string; materials: string; patientId: string; }

const ProcedureExecutionPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'administrador';
    
    const [allPatients, setAllPatients] = useState<Patient[]>([]);
    const [allProcedures, setAllProcedures] = useState<Procedure[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [myRecentProcedures, setMyRecentProcedures] = useState<ExecutionRecord[]>([]);
    
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(null);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [materials, setMaterials] = useState('');
    const [description, setDescription] = useState('');
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [warningOpen, setWarningOpen] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [editWarningOpen, setEditWarningOpen] = useState(false);
    const [macroDialog, setMacroDialog] = useState({ open: false, type: '' });
    const [editMacroOpen, setEditMacroOpen] = useState(false);
    const [currentMacro, setCurrentMacro] = useState<Template>({ id: null, title: '', content: '', category: '' });
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [macroToDelete, setMacroToDelete] = useState<string | null>(null);

    // ✅ HELPER: Obtener headers con autorización
    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    // 1. CARGA INICIAL
    const fetchData = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // Cargar Configuración, Pacientes y Plantillas en paralelo
            const [confRes, patRes, tempRes] = await Promise.all([
                fetch(`${API_URL}/api/config`, { headers }),
                fetch(`${API_URL}/api/patients`, { headers }),
                fetch(`${API_URL}/api/procedure-templates`, { headers })
            ]);

            // Procesar Pacientes
            if (patRes.ok) {
                const patients = await patRes.json();
                setAllPatients(patients);
                console.log(`✅ ${patients.length} pacientes cargados`);
            }

            // Procesar Plantillas
            if (tempRes.ok) {
                const templatesData = await tempRes.json();
                setTemplates(templatesData);
                console.log(`✅ ${templatesData.length} plantillas cargadas`);
            }

            // Procesar configuración
            let specsParam = '';
            if (confRes.ok) {
                const confData = await confRes.json();
                if (confData.favoriteSpecialties && confData.favoriteSpecialties !== '[]') {
                    specsParam = `&specs=${encodeURIComponent(confData.favoriteSpecialties)}`;
                }
            }

            // Cargar procedimientos
            const procRes = await fetch(`${API_URL}/api/procedures?take=2000${specsParam}`, { headers });
            if (procRes.ok) {
                const data = await procRes.json();
                const unique = data.filter((v: any, i: number, a: any[]) => 
                    a.findIndex(t => t.name === v.name) === i
                );
                setAllProcedures(unique);
            }

            // Cargar historial
            const recRes = await fetch(`${API_URL}/api/recipes`, { headers });
            if (recRes.ok) {
                const allData = await recRes.json();
                const filtered = allData.filter((r: any) => 
                    r.type === 'procedure_execution' && (isAdmin || r.doctorId === user?.id)
                );
                setMyRecentProcedures(filtered);
            }

        } catch (err) {
            console.error("Error crítico de carga:", err);
        }
    }, [isAdmin, user?.id]);

    useEffect(() => { 
        fetchData(); 
    }, [fetchData]);

    // ✅ FUNCIÓN handleLoadToEdit
    const handleLoadToEdit = (record: ExecutionRecord) => {
        const patient = allPatients.find(p => p.id === record.patientId);
        setSelectedPatient(patient || null);
        setSelectedProcedure({ id: '', name: record.procedureName });
        setMaterials(record.materials || '');
        setDescription(record.description || '');
        setEditWarningOpen(true);
    };

    // ✅ GUARDAR PROCEDIMIENTO
    const handleConfirmSave = async () => {
        if (!selectedPatient || !selectedProcedure) {
            setError('Debe seleccionar paciente y procedimiento');
            setConfirmOpen(false);
            return;
        }
        
        setConfirmOpen(false);
        setLoading(true);
        
        try {
            const token = localStorage.getItem('token');
            const executionData = {
                patientId: selectedPatient.id,
                procedureName: selectedProcedure.name,
                date, 
                materials, 
                description,
                doctorName: user?.name || 'Médico',
                doctorId: user?.id,
                type: 'procedure_execution'
            };
            
            const res = await fetch(`${API_URL}/api/recipes`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(executionData)
            });
            
            if (!res.ok) throw new Error('Error al guardar');
            
            await res.json();
            
            setMaterials('');
            setDescription('');
            setSelectedProcedure(null);
            setDate(new Date().toISOString().split('T')[0]);
            await fetchData();
            setSuccess('✅ Registro guardado correctamente');
            setError('');
            
        } catch (err) {
            console.error('Error al guardar:', err);
            setError('❌ Error al guardar el registro');
            setSuccess('');
        } finally {
            setLoading(false);
        }
    };

    // ========== GESTIÓN DE MACROS ==========

    const handleOpenCreateMacro = (type: string) => {
        setCurrentMacro({ id: null, title: '', content: '', category: type });
        setEditMacroOpen(true);
    };

    const handleOpenEditMacro = (template: Template) => {
        setCurrentMacro(template);
        setMacroDialog({ open: false, type: '' });
        setEditMacroOpen(true);
    };

    // ✅ GUARDAR MACRO - CON AUTORIZACIÓN
    const handleSaveMacro = async () => {
        if (!currentMacro.title.trim() || !currentMacro.content.trim()) {
            setError('El título y contenido son obligatorios');
            return;
        }

        setLoading(true);
        try {
            const method = currentMacro.id ? 'PUT' : 'POST';
            const url = currentMacro.id 
                ? `${API_URL}/api/procedure-templates/${currentMacro.id}`
                : `${API_URL}/api/procedure-templates`;

            const res = await fetch(url, {
                method,
                headers: getAuthHeaders(), // ✅ CON TOKEN
                body: JSON.stringify(currentMacro)
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || 'Error al guardar plantilla');
            }

            await fetchData();
            setEditMacroOpen(false);
            setSuccess(currentMacro.id ? '✅ Plantilla actualizada' : '✅ Plantilla creada');
            setCurrentMacro({ id: null, title: '', content: '', category: '' });

        } catch (err: any) {
            console.error('Error guardando macro:', err);
            setError(`❌ ${err.message || 'Error al guardar la plantilla'}`);
        } finally {
            setLoading(false);
        }
    };

    // ✅ ELIMINAR MACRO - CON AUTORIZACIÓN
    const handleDeleteMacro = async () => {
        if (!macroToDelete) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/procedure-templates/${macroToDelete}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } // ✅ CON TOKEN
            });

            if (!res.ok) throw new Error('Error al eliminar');

            await fetchData();
            setSuccess('✅ Plantilla eliminada');
            setDeleteConfirmOpen(false);
            setMacroToDelete(null);

        } catch (err) {
            console.error('Error eliminando macro:', err);
            setError('❌ Error al eliminar la plantilla');
        } finally {
            setLoading(false);
        }
    };

    const handleUseMacro = (template: Template) => {
        if (macroDialog.type === 'proc_material') {
            setMaterials(prev => prev + (prev ? '\n' : '') + template.content);
        } else if (macroDialog.type === 'proc_description') {
            setDescription(prev => prev + (prev ? '\n' : '') + template.content);
        }
        setMacroDialog({ open: false, type: '' });
    };

    const getCategoryLabel = (category: string) => {
        const labels: { [key: string]: string } = {
            'proc_material': 'Materiales',
            'proc_description': 'Descripción',
            'illness': 'Enfermedad Actual',
            'physical': 'Examen Físico',
            'evolution': 'Evolución'
        };
        return labels[category] || category;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MedicalServicesIcon sx={{ fontSize: 40 }} />
                    Realizar Procedimiento
                </Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Volver</Button>
            </Box>

            <Grid container spacing={3}>
                {/* PANEL IZQUIERDO: FORMULARIO */}
                <Grid item xs={12} lg={8}>
                    <Paper sx={{ p: 3, borderTop: '5px solid #1976d2' }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={allPatients}
                                    getOptionLabel={(o) => `${o.name} ${o.lastName} (${o.cedula || o.idNumber || ''})`}
                                    value={selectedPatient}
                                    onChange={(_, n) => setSelectedPatient(n)}
                                    renderInput={(p) => <TextField {...p} label="1. Seleccione Paciente" required />}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={allProcedures}
                                    getOptionLabel={(o) => o.name || ""}
                                    onChange={(_, n) => setSelectedProcedure(n)}
                                    value={selectedProcedure}
                                    noOptionsText="No se encontró el procedimiento"
                                    renderInput={(p) => <TextField {...p} label="2. Procedimiento" placeholder="Ej: Biopsia" />}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField 
                                    fullWidth 
                                    type="date" 
                                    label="Fecha" 
                                    InputLabelProps={{ shrink: true }} 
                                    value={date} 
                                    onChange={e => setDate(e.target.value)} 
                                />
                            </Grid>

                            <Grid item xs={12} md={8}>
                                <TextField 
                                    fullWidth 
                                    label="Materiales" 
                                    value={materials} 
                                    onChange={e => setMaterials(e.target.value)}
                                    InputProps={{ 
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title="Usar plantilla">
                                                    <IconButton onClick={() => setMacroDialog({ open: true, type: 'proc_material' })} color="secondary">
                                                        <PlaylistAddIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    multiline 
                                    rows={6} 
                                    label="Descripción y Hallazgos" 
                                    value={description} 
                                    onChange={e => setDescription(e.target.value)}
                                    InputProps={{ 
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                                <Tooltip title="Usar plantilla">
                                                    <IconButton onClick={() => setMacroDialog({ open: true, type: 'proc_description' })} color="primary">
                                                        <AssignmentIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                                variant="contained" 
                                size="large" 
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
                                onClick={() => setConfirmOpen(true)}
                                disabled={loading}
                            >
                                {loading ? 'Guardando...' : 'Guardar en Historia'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* PANEL DERECHO: HISTORIAL */}
                <Grid item xs={12} lg={4}>
                    <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon /> Mis Registros Recientes
                    </Typography>
                    <Paper sx={{ maxHeight: '70vh', overflowY: 'auto', p: 1, bgcolor: '#f8f9fa' }}>
                        {myRecentProcedures.length === 0 ? (
                            <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>No ha realizado registros aún.</Typography>
                        ) : (
                            myRecentProcedures.map((rec) => (
                                <Card key={rec.id} sx={{ mb: 1.5, borderLeft: '4px solid #4caf50' }}>
                                    <Box sx={{ p: 1.5 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">{rec.procedureName}</Typography>
                                        <Typography variant="caption" display="block" color="text.secondary">Fecha: {new Date(rec.date).toLocaleDateString()}</Typography>
                                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption">Dr. {rec.doctorName}</Typography>
                                            <Button size="small" startIcon={<EditIcon />} onClick={() => handleLoadToEdit(rec)}>Cargar</Button>
                                        </Box>
                                    </Box>
                                </Card>
                            ))
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* DIÁLOGOS */}
            <Dialog open={editWarningOpen} onClose={() => setEditWarningOpen(false)}>
                <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>Modo de Edición / Duplicación</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography gutterBottom>
                        Ha cargado un procedimiento previo. Al guardar, <strong>se creará un nuevo registro</strong> en la historia clínica.
                    </Typography>
                </DialogContent>
                <DialogActions><Button onClick={() => setEditWarningOpen(false)} variant="contained">Entendido</Button></DialogActions>
            </Dialog>

            <Dialog open={warningOpen} onClose={() => setWarningOpen(false)}>
                <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>Aviso importante</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography>Si no encuentra el procedimiento, consulte el Catálogo por especialidad o añada uno nuevo allí.</Typography>
                </DialogContent>
                <DialogActions><Button onClick={() => setWarningOpen(false)} variant="contained" fullWidth>Continuar</Button></DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>¿Confirmar Registro?</DialogTitle>
                <DialogContent><Typography>El registro se guardará en la historia clínica del paciente de forma permanente.</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleConfirmSave} variant="contained" disabled={loading}>
                        {loading ? 'Guardando...' : 'Aceptar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DIÁLOGO DE MACROS */}
            <Dialog open={macroDialog.open} onClose={() => setMacroDialog({ open: false, type: '' })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>Plantillas - {getCategoryLabel(macroDialog.type)}</Box>
                    <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={() => handleOpenCreateMacro(macroDialog.type)} sx={{ bgcolor: 'white', color: 'primary.main' }}>
                        Nueva Plantilla
                    </Button>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {templates.filter(t => t.category === macroDialog.type).length === 0 ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            No hay plantillas disponibles.
                            <Button size="small" sx={{ ml: 1 }} onClick={() => handleOpenCreateMacro(macroDialog.type)}>Crear Primera Plantilla</Button>
                        </Alert>
                    ) : (
                        <List>
                            {templates.filter(t => t.category === macroDialog.type).map(template => (
                                <React.Fragment key={template.id}>
                                    <ListItem sx={{ '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, mb: 0.5 }}>
                                        <ListItemText 
                                            primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography fontWeight="bold">{template.title}</Typography>
                                                <Chip label={getCategoryLabel(template.category)} size="small" color="primary" variant="outlined" />
                                            </Box>}
                                            secondary={template.content.length > 100 ? template.content.substring(0, 100) + '...' : template.content}
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Usar"><IconButton edge="end" onClick={() => handleUseMacro(template)} color="primary" sx={{ mr: 1 }}><PlaylistAddIcon /></IconButton></Tooltip>
                                            <Tooltip title="Editar"><IconButton edge="end" onClick={() => handleOpenEditMacro(template)} color="info" sx={{ mr: 1 }}><EditIcon /></IconButton></Tooltip>
                                            <Tooltip title="Eliminar"><IconButton edge="end" onClick={() => { setMacroToDelete(template.id); setDeleteConfirmOpen(true); }} color="error"><DeleteIcon /></IconButton></Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setMacroDialog({ open: false, type: '' })}>Cerrar</Button></DialogActions>
            </Dialog>

            {/* DIÁLOGO CREAR/EDITAR MACRO */}
            <Dialog open={editMacroOpen} onClose={() => setEditMacroOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: currentMacro.id ? 'info.main' : 'success.main', color: 'white' }}>
                    {currentMacro.id ? 'Editar Plantilla' : 'Nueva Plantilla'}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Título de la Plantilla" value={currentMacro.title} onChange={(e) => setCurrentMacro({ ...currentMacro, title: e.target.value })} placeholder="Ej: Sutura simple 3 puntos" required />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={6} label="Contenido" value={currentMacro.content} onChange={(e) => setCurrentMacro({ ...currentMacro, content: e.target.value })} placeholder="Escriba aquí el contenido..." required />
                        </Grid>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                                Categoría: <strong>{getCategoryLabel(currentMacro.category)}</strong>
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditMacroOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveMacro} variant="contained" disabled={loading || !currentMacro.title.trim() || !currentMacro.content.trim()} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}>
                        {loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DIÁLOGO ELIMINAR */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>¿Eliminar Plantilla?</DialogTitle>
                <DialogContent sx={{ mt: 2 }}><Typography>Esta acción no se puede deshacer.</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeleteMacro} variant="contained" color="error" disabled={loading}>{loading ? 'Eliminando...' : 'Eliminar'}</Button>
                </DialogActions>
            </Dialog>

            {/* NOTIFICACIONES */}
            <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>{success}</Alert>
            </Snackbar>
            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>
        </Box>
    );
};

export default ProcedureExecutionPage;